import { track, trackFormSubmit, ANALYTICS_EVENTS, textParam, resolveSectionContext } from "@theme/analytics";

/**
 * Where a native submit leaves a note for itself, so the confirmation can be
 * shown when the shopper comes back. Shopify's spam challenge builds its
 * return_path from the PATH ONLY and drops the query string, so the
 * customer_posted flag the redirect normally carries never survives a
 * challenge (MA-2348, seen on prd usa: return_path=%2Fpages%2Fabout).
 */
const PENDING_KEY = "ma:email-signup-pending";

/**
 * How long that note stays good for. Long enough to read and solve a captcha,
 * short enough that an abandoned challenge does not surface a code later.
 */
const PENDING_TTL = 5 * 60 * 1000;

/**
 * A portal id is alphanumeric with hyphens and underscores, mirroring the
 * validation in portal-manager.js. Applied before the portal is written into the
 * submission, so a malformed cookie can never be spliced into a customer tag.
 */
const PORTAL_ID_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;

/**
 * Where the portal rides along in the submission.
 *
 * Shopify's customer form has no field of its own for a portal and cannot write
 * customer metafields, so the portal travels as a tag on the customer the signup
 * creates. Verified to survive the post, including the detour through Shopify's
 * spam challenge that the async path falls back to.
 */
const PORTAL_TAG_FIELD = "contact[tags]";

/**
 * The tag the portal is written as.
 *
 * The `portal-id=` prefix is not cosmetic: a Shopify Flow workflow matches on it
 * to copy the portal onto the customer's own metafield, then removes the tag.
 * Changing this format stops that workflow matching, and it fails silently —
 * signups would simply go back to being credited to the corporate default.
 *
 * @param {string} portalId
 * @returns {string}
 */
function portalTag(portalId) {
  return `portal-id=${portalId}`;
}

class EmailSignupForm extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {HTMLInputElement | null} */
  input = null;

  /** @type {HTMLButtonElement | null} */
  button = null;

  /** @type {HTMLElement | null} */
  errorMessage = null;

  /** @type {HTMLElement | null} */
  errorText = null;

  /** @type {HTMLElement | null} */
  successMessage = null;

  /** @type {boolean} */
  submitting = false;

  /** @type {boolean} */
  nativeFallback = false;

  connectedCallback() {
    this.form = this.querySelector("form");
    this.input = this.querySelector('input[type="email"]');
    this.button = this.querySelector(".email-signup__button");
    this.errorMessage = this.querySelector("[data-signup-error]");
    this.errorText = this.querySelector("[data-signup-error-text]");
    this.successMessage = this.querySelector("[data-signup-success]");

    if (!this.form || !this.input) return;

    this.form.addEventListener("submit", this.onSubmit);
    // Written at load as well as on submit: Shopify's captcha script listens on
    // this same form, and when it wins the ordering (or re-posts via form.submit(),
    // which fires no submit event) onSubmit never runs. The field has to be in the
    // form before that happens, or the post goes out untagged (MA-3054).
    this.applyPortalTag();
    this.showSuccessAfterNativeSubmit();
    this.scrollToNativeResult();
  }

  /**
   * The native flow sends the browser back to #contact_form, the <form>'s own
   * id. When showSuccess() has just hidden that form there is no box left for
   * the browser's fragment jump to land on, so the page stays at the top with
   * the footer's confirmation a whole page below the fold. Take over the jump
   * and scroll to this element, which stays visible, whenever it holds a
   * server-rendered or freshly revealed result.
   */
  scrollToNativeResult() {
    // The popup sits in a fixed dialog: it is in view wherever the page is.
    if (this.closest("dialog")) return;

    const hasMessage = this.querySelector(
      '[id^="Email-signup__message-"], [data-signup-success]:not([hidden]), [data-signup-error]:not([hidden])'
    );
    if (!hasMessage) return;

    const scroll = () => this.scrollIntoView({ block: "center" });
    requestAnimationFrame(scroll);
    // Content above (lazy images, embeds) can still grow after the first pass
    // and push the footer down, so settle again once everything has loaded.
    if (document.readyState !== "complete") window.addEventListener("load", scroll, { once: true });
  }

  /**
   * The native submission flow (captcha challenge, no-JS) ends in a redirect.
   * Liquid doesn't reliably re-render the confirmation on that reload (the
   * challenge replay can lose the posted form id), so surface the success
   * state here instead — unless the server-rendered confirmation is already
   * on the page. Two signals, because neither covers both routes: the
   * redirect's customer_posted flag, which a spam challenge strips along with
   * the rest of the query string, and the note markPending() leaves behind,
   * which survives it.
   */
  showSuccessAfterNativeSubmit() {
    const posted = new URLSearchParams(window.location.search).get("customer_posted") === "true";
    // takePending() always runs: a stale note is cleared whichever signal wins.
    const pending = this.takePending();
    if (!posted && !pending) return;

    const serverSuccess = this.querySelector('[id^="Email-signup__message-success-"]');

    // A page can hold two signup forms (the footer block and the popup) but
    // only one was posted — and both see customer_posted. Shopify re-renders
    // the confirmation inside the posted form, so when some OTHER form on the
    // page carries it, this one stays out of the way. Without this the single
    // signup is reported twice, under the wrong leadSource half the time.
    if (!serverSuccess && document.querySelector('[id^="Email-signup__message-success-"]')) return;

    // customer_posted survives a refresh, which would replay both the success
    // state and its lead event. Stripping it also makes the first form to reach
    // this point the only one that can: the rest read a URL without the flag.
    this.stripPostedParam();

    // Tracked here rather than in showSuccess(): when the server renders the
    // confirmation itself showSuccess() is skipped, but the signup happened.
    this.trackLead("native");

    if (!serverSuccess) this.showSuccess();
  }

  /**
   * Removes customer_posted from the address bar without a navigation, so
   * reloading a post-signup page is not a second signup.
   */
  stripPostedParam() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("customer_posted");
      window.history.replaceState(window.history.state, "", url.toString());
    } catch {
      // History API unavailable — the sessionStorage-free cost is a possible
      // duplicate on manual refresh, which is preferable to throwing here.
    }
  }

  /**
   * Which signup surface this is. A page can hold the footer block and the
   * popup at once, and only the one that was submitted may claim the note.
   * @returns {string}
   */
  pendingId() {
    return this.dataset.gaLeadSource || this.form?.id || "email_signup";
  }

  /**
   * Notes that this form is handing over to the native flow, with the path it
   * expects to come back to. The path is what keeps an abandoned challenge
   * from showing a code somewhere else on the site later.
   */
  markPending() {
    try {
      window.sessionStorage.setItem(
        PENDING_KEY,
        JSON.stringify({ source: this.pendingId(), path: window.location.pathname, ts: Date.now() })
      );
    } catch {
      // Storage unavailable (private mode, blocked cookies): the query flag
      // still covers every route except the challenge.
    }
  }

  /**
   * Consumes a note left by this form, for this page, recently enough.
   * @returns {boolean}
   */
  takePending() {
    try {
      const raw = window.sessionStorage.getItem(PENDING_KEY);
      if (!raw) return false;

      const { source, path, ts } = JSON.parse(raw);
      const mine = source === this.pendingId() && path === window.location.pathname;
      const fresh = typeof ts === "number" && Date.now() - ts >= 0 && Date.now() - ts < PENDING_TTL;

      // A note that is stale, or for another surface, is dropped rather than
      // left to fire on some later page.
      if (!mine || !fresh) {
        if (!fresh) window.sessionStorage.removeItem(PENDING_KEY);
        return false;
      }

      window.sessionStorage.removeItem(PENDING_KEY);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Reports a captured email lead. This element backs every email-only opt-in
   * surface — the footer newsletter block and the exclusive-offer popup — so
   * `leadSource` is what tells them apart downstream.
   * @param {"async"|"native"} method - Which submit path completed.
   */
  trackLead(method) {
    const { sectionId, sectionType, analyticsId } = resolveSectionContext(this);

    track(ANALYTICS_EVENTS.EMAIL_LEAD, {
      leadSource: this.dataset.gaLeadSource || sectionType || "email_signup",
      leadFormId: this.form?.id || "",
      leadHeading: textParam(this.closest("[data-signup-scope]")?.querySelector("h1, h2, h3")?.textContent),
      submitMethod: method,
      sectionId: sectionId,
      sectionType: sectionType,
      analyticsId: analyticsId,
    });

    // The generic form funnel counts this signup too, so a form that reports a
    // start also reports its submit. ng_email_lead stays the semantic lead
    // event; ng_form_submit is the funnel's other end. Both are reported from
    // here, so the two can never disagree about whether the signup happened.
    if (this.form) trackFormSubmit(this.form, { submitMethod: method });
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.onSubmit);
  }

  /**
   * Submit the signup form asynchronously. Invalid/empty emails never reach
   * this handler — native constraint validation blocks the submit event.
   * @param {SubmitEvent} event
   */
  onSubmit = (event) => {
    // Ahead of every early return below, because the submission that carries the
    // portal is just as often one of the native ones that leave this handler
    // immediately. This refreshes the field connectedCallback() wrote at load:
    // by now portal-manager has reconciled the URL and the cookie, so this is the
    // final answer. The field stays on the form once written, so the re-fired
    // submit from submitNatively() still carries it.
    this.applyPortalTag();

    // In fallback mode, step aside so the native submission (with the captcha
    // wiring from Shopify.captcha.protect) runs untouched.
    if (this.nativeFallback) return;

    // The captcha API isn't in the editor's ambient Shopify types yet.
    const captcha = /** @type {any} */ (window.Shopify)?.captcha;

    // Spam protection is active but the protect API isn't available: an async
    // post can't carry a valid captcha token and gets rejected as bot traffic,
    // so let the platform's native flow handle the submission.
    if (!captcha?.protect && this.captchaProtected()) return;

    event.preventDefault();

    if (this.submitting || !this.form) return;

    // Shopify's supported path for programmatic submission of protected
    // forms: protect() invokes the callback once captcha is ready, so the
    // token travels with the post instead of triggering a challenge redirect.
    if (captcha?.protect) {
      captcha.protect(this.form, () => this.submitAsync());
    } else {
      this.submitAsync();
    }
  };

  /**
   * Put the portal the visitor arrived through into the submission.
   *
   * This form creates the Shopify customer, and the registration service fills in
   * the attribution from the record it sees. With no portal in the post it has
   * nothing to go on and the signup lands on the corporate default, so a shopper
   * who arrives through a distributor's link and subscribes is credited to
   * corporate rather than to them.
   *
   * The field is built here rather than rendered in the template because the
   * portal is only ever known in the browser: for an anonymous visitor it lives
   * in a cookie or the URL, and Liquid can read neither. It is built only when a
   * portal actually resolves, so a visitor without one posts no empty tag and
   * nothing about their customer record changes.
   *
   * Runs at load and again on submit. When the second pass resolves nothing the
   * field from the first pass is removed, so a portal that went away in between
   * (a ?portal_id= clear, a cookie expiring) is not posted as stale.
   */
  applyPortalTag() {
    if (!this.form) return;

    const portalId = this.portalId();

    if (portalId) {
      this.setHiddenField(PORTAL_TAG_FIELD, portalTag(portalId));
    } else {
      this.removeHiddenField(PORTAL_TAG_FIELD);
    }
  }

  /**
   * Resolve the portal, most trustworthy source first.
   *
   * This runs from connectedCallback(), which is before portal-manager.js has
   * reconciled the URL against the cookie on DOMContentLoaded, so it cannot lean
   * on window.PortalManager.getPortalId(): at that point that call returns the
   * cookie, which is the previous portal whenever the visitor arrived through a
   * new portal link. Resolve from the raw sources instead, in portal-manager's own
   * order:
   *
   * 1. The signed-in customer's portal, which Liquid writes to
   *    window.customerPortalId synchronously. portal-manager ignores the URL and
   *    the cookie entirely for such a customer, and so does this.
   * 2. The URL param. On the very first page view of a portal link it is the
   *    only place the new portal exists. A present-but-empty ?portal_id= is
   *    portal-manager's clear command, so it resolves to no portal rather than
   *    falling back to the cookie that is about to be deleted.
   * 3. The cookie, for every later page view.
   *
   * first-time-discount-popup.js gates on the same three sources in the same order.
   * @returns {string}
   */
  portalId() {
    const customerPortalId = /** @type {any} */ (window).customerPortalId;
    if (this.validPortalId(customerPortalId)) return String(customerPortalId).trim();

    const params = new URLSearchParams(window.location.search);
    if (params.has("portal_id")) {
      const urlPortalId = params.get("portal_id");
      if (urlPortalId === "") return "";
      if (this.validPortalId(urlPortalId)) return String(urlPortalId).trim();
    }

    const cookiePortalId = this.getCookie("portal_id");
    if (this.validPortalId(cookiePortalId)) return cookiePortalId.trim();

    return "";
  }

  /**
   * @param {unknown} candidate
   * @returns {boolean}
   */
  validPortalId(candidate) {
    return PORTAL_ID_PATTERN.test(String(candidate || "").trim());
  }

  /**
   * Add a hidden field to the form, or update it if a previous submit already
   * added it, so a retry after an error never stacks duplicates.
   * @param {string} name
   * @param {string} value
   */
  setHiddenField(name, value) {
    if (!this.form) return;

    let field = /** @type {HTMLInputElement | null} */ (this.form.querySelector(`input[name="${name}"]`));

    if (!field) {
      field = document.createElement("input");
      field.type = "hidden";
      field.name = name;
      this.form.appendChild(field);
    }

    field.value = value;
  }

  /**
   * Drop a hidden field an earlier applyPortalTag() pass added. Only fields this
   * element created are touched: the template renders none under this name.
   * @param {string} name
   */
  removeHiddenField(name) {
    this.form?.querySelector(`input[type="hidden"][name="${name}"]`)?.remove();
  }

  /**
   * Read a cookie value by name.
   * @param {string} name
   * @returns {string}
   */
  getCookie(name) {
    const match = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]*)"));
    const value = match?.[1];
    return value === undefined ? "" : decodeURIComponent(value);
  }

  async submitAsync() {
    if (this.submitting || !this.form) return;
    this.submitting = true;

    this.hideMessages();
    if (this.button) this.button.disabled = true;

    try {
      const response = await fetch(this.form.action, {
        method: "POST",
        body: new FormData(this.form),
        headers: { Accept: "text/html" },
      });

      // Shopify guards customer form posts with spam protection: a fetch that
      // carries no captcha token is either redirected to a challenge page
      // (which can't be completed over fetch) or rejected with a 4xx. Both
      // need the native submission flow, not an error message.
      if (new URL(response.url, window.location.origin).pathname.includes("/challenge")) {
        this.submitNatively();
        return;
      }

      const serverError = await this.extractServerError(response);

      if (serverError) {
        this.showError(serverError);
      } else if (response.ok) {
        this.trackLead("async");
        this.showSuccess();
      } else if (response.status === 400 || response.status === 422) {
        this.submitNatively();
      } else {
        this.showError();
      }
    } catch (error) {
      console.error("Email signup failed:", error);
      this.showError();
    } finally {
      this.submitting = false;
      if (this.button) this.button.disabled = false;
    }
  };

  submitNatively() {
    if (!this.form) return;

    this.markPending();
    this.nativeFallback = true;
    // requestSubmit (unlike form.submit) re-fires the submit event, so
    // Shopify's captcha script can intercept and complete the signup.
    this.form.requestSubmit();
  }

  /**
   * Shopify's spam protection injects a hidden captcha token field into
   * protected forms. Its presence means submissions must go through the
   * native flow.
   * @returns {boolean}
   */
  captchaProtected() {
    return Boolean(
      this.form?.querySelector(
        '[name="h-captcha-response"], [name="g-recaptcha-response"], [name="recaptcha-v3-token"]'
      )
    );
  }

  /**
   * Shopify re-renders the posted page with the form errors inlined, so pull
   * our block's server-rendered error message out of the response HTML.
   * @param {Response} response
   * @returns {Promise<string | null>}
   */
  async extractServerError(response) {
    try {
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, "text/html");
      const error = doc.querySelector('.email-signup__message[id^="Email-signup__message-error-"]');

      return error?.textContent?.trim() || null;
    } catch {
      return null;
    }
  }

  hideMessages() {
    this.errorMessage?.setAttribute("hidden", "");
    this.successMessage?.setAttribute("hidden", "");
    this.input?.removeAttribute("aria-invalid");
    this.input?.removeAttribute("aria-describedby");
  }

  /** @param {string} [message] */
  showError(message) {
    if (message && this.errorText) this.errorText.textContent = message;

    this.errorMessage?.removeAttribute("hidden");

    if (this.errorMessage?.id) {
      this.input?.setAttribute("aria-invalid", "true");
      this.input?.setAttribute("aria-describedby", this.errorMessage.id);
    }
  }

  showSuccess() {
    // Replace the form with the confirmation: only the success message stays.
    // The promo and privacy texts live outside this element but inside the
    // enclosing [data-signup-scope] wrapper, tagged with a data attribute.
    this.form?.setAttribute("hidden", "");

    const scope = this.closest("[data-signup-scope]") || this;
    scope.querySelectorAll("[data-signup-hide-on-success]").forEach((element) => {
      element.setAttribute("hidden", "");
    });

    this.successMessage?.removeAttribute("hidden");
  }
}

if (!customElements.get("email-signup-form")) {
  customElements.define("email-signup-form", EmailSignupForm);
}
